<template>
  <div class="!relative">
    <Input
      v-bind="$attrs"
      ref="input"
      v-model.noWhitespace="inputValue"
      autocomplete="new-password"
      type="password"
      tag="input"
      :error="!hasFocus ? error : ''"
      @blur="onBlur"
      @update:model-value="(val) => setValue(val, false)"
      @focus="onFocus"
      @click="() => emit('click')"
    />
    <div v-if="hasFocus" class="bg-bgr-primary shadow-on-top absolute z-10 mt-1 w-full rounded-xl px-2.5 py-3">
      <div class="mb-3">
        <div class="mb-1 font-medium">{{ t("passwords_must_contain") }}</div>
        <ul
          class="list-none space-y-1 pl-4 font-normal"
          :class="{
            '[&>li]:-ml-5 [&>li]:flex [&>li]:items-center [&>li]:gap-2 [&>li]:marker:aspect-square [&>li]:marker:text-xs [&>li]:before:inline-block [&>li]:before:size-4 [&>li]:before:bg-cover [&>li]:before:bg-center [&>li]:before:bg-no-repeat': true,
          }"
        >
          <li
            class="password-requirment"
            :class="{
              'text-fgr-success! before:bg-(image:--check)': fulFills('length'),
            }"
          >
            {{ t("minimum_n_characters", { n: min }) }}
          </li>
          <li
            class="password-requirment"
            :class="{
              'text-fgr-success! before:bg-(image:--check)': fulFills('case'),
            }"
          >
            {{ t("uppercase_and_lowercase") }}
          </li>
        </ul>
      </div>
      <div>
        <div>
          <div class="mb-2">{{ t("strength") }}</div>
          <PasswordStrength :password="inputValue as string" />
          <p class="text-fgr-secondary mt-2">
            {{ t("avoid_passwords") }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from "vue";

  const props = withDefaults(
    defineProps<{
      active?: boolean;
      disabled?: boolean;
      error?: string;
      min?: number;
      name?: string;
      selected?: boolean;
    }>(),
    {
      active: false,
      disabled: false,
      error: undefined,
      min: 8,
      name: "",
      selected: false,
    }
  );

  const { t } = useI18n();
  const _modelValue = defineModel<string>();

  const hasFocus = ref(false);

  const onFocus = () => {
    hasFocus.value = true;
  };

  const onBlur = (event: Event) => {
    hasFocus.value = false;
    handleBlur(event, true);
  };

  const {
    value: inputValue,
    errorMessage,
    handleBlur,
    setValue,
  } = useField(() => props.name, undefined, {
    syncVModel: props.name ? false : true,
    controlled: !!props.name,
  });

  const fulFills = (rule) => {
    if (!inputValue.value || inputValue.value.length === 0) return false;

    switch (rule) {
      case "length":
        return inputValue.value.length >= props.min;
      case "case":
        return /[a-z]/.test(inputValue.value) && /[A-Z]/.test(inputValue.value);
      default:
        return false;
    }
  };

  const error = computed(() => {
    return props.error || errorMessage.value;
  });

  const input = ref<HTMLElement | null>(null);

  const el = computed(() => {
    return input.value?.input;
  });

  const emit = defineEmits(["click"]);

  const isFullfilled = computed(() => {
    return fulFills("length") && fulFills("case");
  });

  defineExpose({
    el,
    isFullfilled,
  });
</script>
